import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { CulturalIndex } from "@common/ADAPT.Common.Model/organisation/cultural-index";
import { Person } from "@common/ADAPT.Common.Model/person/person";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { CulturalLeadershipFrameworkAuthService } from "@org-common/lib/cultural-leadership/cultural-leadership-framework-auth.service";
import { CulturalLeadershipFrameworkUiService } from "app/features/culture/cultural-leadership/cultural-leadership-framework-ui.service";
import { CulturalIndexAnalysisDataService } from "app/features/people/cultural-index/cultural-index-analysis-data.service";
import { ContentReadyEvent } from "devextreme/ui/accordion";
import { ChartSeries } from "devextreme/viz/common";
import { BaseGaugeRangeContainer } from "devextreme/viz/gauges/base_gauge";
import { switchMap } from "rxjs/operators";
import { CulturalIndexService, ICulturalIndexStatistics } from "../cultural-index.service";
import { CulturalIndexAuthService } from "../cultural-index-auth.service";
import { CulturalIndexFilterService } from "../cultural-index-filter/cultural-index-filter.service";
import { CulturalIndexUiService } from "../cultural-index-ui.service";

interface ICulturalIndexStatisticAccordionEntry {
    text: string;
    people: Person[];
}

@Component({
    selector: "adapt-ci-orbit-statistics",
    templateUrl: "./ci-orbit-statistics.component.html",
    styleUrl: "./ci-orbit-statistics.component.css",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class CulturalIndexOrbitStatisticsComponent extends BaseComponent implements OnInit, OnChanges {
    @Input() public date?: Date;

    public canSeeData = false;

    public activePeopleCount?: number;
    public statisticsData?: ICulturalIndexStatistics;
    public statisticsAccordion?: ICulturalIndexStatisticAccordionEntry[];

    public isLoading = true;

    public rangeContainer: BaseGaugeRangeContainer = {
        ranges: [],
    };

    public series: ChartSeries[] = [];

    public CatchupStatusPalette = CulturalLeadershipFrameworkUiService.CulturalLeadershipFrameworkPalette.simpleSet;

    public constructor(
        public culturalIndexUiService: CulturalIndexUiService,
        private culturalIndexService: CulturalIndexService,
        private culturalIndexFilterService: CulturalIndexFilterService,
        private culturalIndexAnalysisDataService: CulturalIndexAnalysisDataService,
        private culturalIndexAuthService: CulturalIndexAuthService,
        rxjsBreezeService: RxjsBreezeService,
        private clfAuthService: CulturalLeadershipFrameworkAuthService,
    ) {
        super();

        rxjsBreezeService.entityTypeChangedInSave(CulturalIndex)
            .pipe(
                switchMap(() => this.getDataAndRefreshCharts()),
                this.takeUntilDestroyed(),
            )
            .subscribe();

        this.culturalIndexFilterService.filterListener
            .pipe(
                switchMap(() => this.getDataAndRefreshCharts()),
                this.takeUntilDestroyed(),
            )
            .subscribe();
    }


    public async ngOnInit() {
        this.canSeeData = this.culturalIndexAuthService.currentPersonHasAtLeastQuantitativeAccess()
            || await this.clfAuthService.currentPersonIsCulturalLeader();

        await this.getDataAndRefreshCharts();
        this.setupRangeContainer();
        this.setupSeries();
    }

    public async ngOnChanges(changes: SimpleChanges) {
        if (changes.date) {
            await this.getDataAndRefreshCharts();
        }
    }

    public get culturalIndexesHaveBeenRecorded() {
        return this.statisticsData && this.statisticsData.averageCulturalIndex > 0;
    }

    public onAccordionReady(e: ContentReadyEvent) {
        setTimeout(() => e.component.updateDimensions(), 50);
    }

    @Autobind
    private async getDataAndRefreshCharts() {
        const filterParameters = { ...this.culturalIndexFilterService.filter, date: this.date };
        const data = await this.culturalIndexService.promiseToGetCulturalIndexAnalysisData(filterParameters, false, true);
        this.statisticsData = this.culturalIndexService.generateCulturalIndexStatistics(data);
        this.statisticsAccordion = this.getStatisticsAccordionEntries(this.statisticsData);
        this.activePeopleCount = await this.culturalIndexAnalysisDataService.getActivePeopleCount(filterParameters);
        this.isLoading = false;
    }

    private getStatisticsAccordionEntries(data: ICulturalIndexStatistics): ICulturalIndexStatisticAccordionEntry[] {
        const entries: ICulturalIndexStatisticAccordionEntry[] = [];

        if (data.missingCulturalIndexes.length > 0) {
            entries.push({
                text: `${data.missingCulturalIndexes.length} people without a cultural index`,
                people: data.missingCulturalIndexes,
            });
        }

        if (data.excludedPeople.length > 0) {
            entries.push({
                text: `${data.excludedPeople.length} people excluded from cultural statistics`,
                people: data.excludedPeople,
            });
        }

        return entries;
    }

    @Autobind
    public setupRangeContainer() {
        for (const valueState of this.culturalIndexUiService.valueStates) {
            this.rangeContainer.ranges?.push({
                startValue: valueState.lower,
                endValue: valueState.upper,
                color: valueState.fgColor,
            });
        }
    }

    @Autobind
    private setupSeries() {
        for (const valueState of this.culturalIndexUiService.valueStates) {
            this.series.push({
                valueField: valueState.name + "Count",
                name: valueState.state,
                color: valueState.fgColor,
            });
        }
    }
}
